import { useEffect, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { storeName } from "../lib/labels";

export default function StoreMultiSelect({ outlets, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const allSelected = !value || value.length === 0;
  const single = outlets.length === 1;
  const selectedNames = allSelected
    ? (single ? storeName(outlets[0].name) : "All stores")
    : outlets.filter((o) => value.includes(o.id)).map((o) => storeName(o.name)).join(" + ") || "None";

  function toggle(id) {
    if (allSelected) {
      onChange([id]);
      return;
    }
    const next = value.includes(id) ? value.filter((x) => x !== id) : [...value, id];
    if (next.length === 0 || next.length === outlets.length) onChange([]);
    else onChange(next);
  }

  return (
    <div className="relative max-md:w-full" ref={ref} data-testid="store-multi-select">
      <div className="bm-eyebrow mb-2">Stores</div>
      <button
        data-testid="store-select-trigger"
        onClick={() => !single && setOpen((v) => !v)}
        disabled={single}
        className="px-3 py-2 border border-[#0a0a0a] font-mono-bm text-sm flex items-center gap-2 hover:bg-[#0a0a0a]/5 min-w-[220px] max-md:w-full justify-between disabled:cursor-default disabled:hover:bg-transparent"
      >
        <span className="truncate">{selectedNames}</span>
        {!single && <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""} flex-shrink-0`} />}
      </button>

      {open && (
        <div
          data-testid="store-select-popover"
          className="absolute z-40 mt-2 w-[280px] bg-[#f5f2eb] border border-[#0a0a0a] shadow-[8px_8px_0_0_#0a0a0a] bm-popover-mobile"
        >
          <button
            onClick={() => { onChange([]); setOpen(false); }}
            data-testid="store-all"
            className="w-full flex items-center gap-3 px-4 py-3 border-b border-[#0a0a0a] hover:bg-[#0a0a0a]/5"
          >
            <span className={`w-4 h-4 border-2 border-[#0a0a0a] flex items-center justify-center flex-shrink-0 ${allSelected ? "bg-[#0a0a0a]" : "bg-transparent"}`}>
              {allSelected && <Check size={11} className="text-[#f5f2eb]" strokeWidth={3} />}
            </span>
            <span className="text-sm font-medium">All stores</span>
          </button>
          {outlets.map((o) => {
            const checked = !allSelected && value.includes(o.id);
            return (
              <button
                key={o.id}
                onClick={() => toggle(o.id)}
                data-testid={`store-option-${o.id}`}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#0a0a0a]/5 text-left"
              >
                <span className={`w-4 h-4 border-2 border-[#0a0a0a] flex items-center justify-center flex-shrink-0 ${checked ? "bg-[#c72c2c] border-[#c72c2c]" : "bg-transparent"}`}>
                  {checked && <Check size={11} className="text-[#f5f2eb]" strokeWidth={3} />}
                </span>
                <span className="text-sm truncate">{storeName(o.name)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
